import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/composites/PageHeader';
import { UnmatchedGroupsTable } from '@/components/composites/UnmatchedGroupsTable';
import { EmptyState } from '@/components/primitives/EmptyState';
import { useAppContext } from '@/context/AppContext';
import { useToken } from '@/lib/useToken';
import { apiFetch } from '@/lib/api';
import { formatDayShort } from '@/lib/dates';
import { EMPLOYEES_WITH_STATS } from '@/data/employees';
import { REVIEW_ITEMS, REVIEW_ITEMS_TOTAL } from '@/data/employeeDetail';
import type { ReviewItem } from '@/data/employeeDetail';
import type { EmployeeFallbackSamplesResponse } from '@/types/dashboard';
import styles from './EmployeeReviewPage.module.css';

/**
 * Task 188 — full-list drill-down for the "Needs Your Review" banner on
 * /employees/:employeeId. Shares its table with the detail page's top-N
 * summary via `UnmatchedGroupsTable`. Visibility (per-tenant + per-member
 * employee filter) is enforced server-side; the endpoint's existing 404
 * path handles "employee not visible" without leaking which it is.
 */
export function EmployeeReviewPage() {
  const { employeeId } = useParams<{ employeeId: string }>();
  const { mockupMode, currentOrg, dateRange } = useAppContext();
  const getToken = useToken();
  const orgId = currentOrg.id;

  // Live mode: pull every unmatched group for the employee. The server
  // clamps `limit` to `DASHBOARD_MAX_LIMIT` (config.dashboard.maxLimit) —
  // pass MAX_SAFE_INTEGER so the clamp is the only ceiling and no new
  // tier-2 constant is introduced on the frontend (CONVENTIONS.md).
  const fallbackQuery = useQuery({
    queryKey: ['employeeFallbackSamplesAll', orgId, employeeId, dateRange],
    queryFn: async () => {
      const token = await getToken();
      const qs = new URLSearchParams();
      qs.set('limit', String(Number.MAX_SAFE_INTEGER));
      if (dateRange.start) qs.set('startDate', dateRange.start);
      if (dateRange.end) qs.set('endDate', dateRange.end);
      return apiFetch<EmployeeFallbackSamplesResponse>(
        `/api/v1/orgs/${orgId}/employees/${employeeId}/events/fallback-samples?${qs.toString()}`,
        token,
      );
    },
    enabled: !mockupMode && !!orgId && !!employeeId,
  });

  // Live mode also needs the employee's display name. The detail page reads
  // it off `/dashboard/employees/:id/projects` — for this page we use the
  // org roster so we can render the name even when the employee has zero
  // projects in the date range.
  const employeeQuery = useQuery({
    queryKey: ['employeesRoster', orgId],
    queryFn: async () => {
      const token = await getToken();
      return apiFetch<{ employees: Array<{ id: string; name: string }> }>(
        `/api/v1/orgs/${orgId}/employees`,
        token,
      );
    },
    enabled: !mockupMode && !!orgId,
  });

  if (mockupMode) {
    const employee = EMPLOYEES_WITH_STATS.find((e) => e.id === employeeId);
    if (!employee) {
      return (
        <>
          <PageHeader title="Employee not found" />
          <EmptyState
            title="Employee not found"
            description="This employee does not exist in the current organization."
          />
        </>
      );
    }
    return (
      <ReviewPageContent
        employeeId={employee.id}
        employeeName={employee.name}
        items={REVIEW_ITEMS}
        totalGroups={REVIEW_ITEMS_TOTAL}
      />
    );
  }

  if (fallbackQuery.isLoading || employeeQuery.isLoading) {
    return (
      <>
        <PageHeader title="Loading…" />
        <EmptyState title="Loading…" description="" />
      </>
    );
  }

  const liveEmployee = employeeQuery.data?.employees.find((e) => e.id === employeeId);
  if (fallbackQuery.isError || !liveEmployee) {
    return (
      <>
        <PageHeader title="Employee not found" />
        <EmptyState
          title="Employee not found"
          description="This employee does not exist in the current organization."
        />
      </>
    );
  }

  const samples = fallbackQuery.data?.samples ?? [];
  const totalGroups = fallbackQuery.data?.totalGroups ?? 0;
  const items: ReviewItem[] = samples.map((s) => {
    const first = formatDayShort(s.firstOccurredAt);
    const last = formatDayShort(s.lastOccurredAt);
    return {
      app: s.app,
      title: s.title,
      screenshots: s.eventCount,
      sessions: s.sessions,
      time: `${s.totalHours.toFixed(1)}h`,
      lastSeen: last,
      dateRange: first === last ? first : `${first} – ${last}`,
    };
  });

  return (
    <ReviewPageContent
      employeeId={liveEmployee.id}
      employeeName={liveEmployee.name}
      items={items}
      totalGroups={totalGroups}
    />
  );
}

function ReviewPageContent({
  employeeId,
  employeeName,
  items,
  totalGroups,
}: {
  employeeId: string;
  employeeName: string;
  items: readonly ReviewItem[];
  totalGroups: number;
}) {
  return (
    <>
      <PageHeader title={`${employeeName} — Unmatched activity`} />
      <div className={styles.backLink}>
        <Link to={`/employees/${employeeId}`}>&larr; Back to {employeeName}</Link>
      </div>
      {totalGroups === 0 ? (
        <EmptyState
          title="No unmatched activity"
          description="Every event in this date range has a matching rule."
        />
      ) : (
        <UnmatchedGroupsTable
          items={items}
          mode="all"
          totalGroups={totalGroups}
          employeeId={employeeId}
        />
      )}
    </>
  );
}
